import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import _, { identity } from 'lodash'
import { SelectOption } from 'react-spreadsheet-import/types/types'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useQueryList } from '@percent/admin-dashboard/common/hooks'
import { Organisation } from '@percent/admin-dashboard/api/types'
import { createShortLink } from '@percent/utility'
import countries from '@percent/admin-dashboard/i18n/data/countries'
import i18n from '@percent/admin-dashboard/i18n/config'

const THROTTLING_TIME = 500

export function useOrganisationSearchBox() {
  const { adminService } = useServices()
  const { t } = useTranslation()

  const [query, setQuery] = useState<string>('')

  const shortLanguage = i18n.language.split('-')[0]

  const [{ dataOrNull: organisationsData, isLoading }, { query: organisationsQuery }] = useQueryList(
    adminService.getOrganisations,
    {
      pageSize: 25
    }
  )

  const getOrganisationDescription = useCallback(
    (organisation: Organisation): string => {
      return [
        organisation.website ? createShortLink(organisation.website) : undefined,
        `${t('typography.id')}: ${organisation.registryId}`,
        countries.getName(organisation.countryCode, shortLanguage)
      ]
        .filter(identity)
        .join(' | ')
    },
    [shortLanguage, t]
  )

  const searchResults: SelectOption[] = useMemo(() => {
    return organisationsData
      ? organisationsData.map(organisation => ({
          value: organisation.id,
          label: organisation.name,
          description: getOrganisationDescription(organisation)
        }))
      : []
  }, [organisationsData, getOrganisationDescription])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOrganisationsQuery = useCallback(
    _.debounce<any>((filter: string) => {
      organisationsQuery({ query: filter })
    }, THROTTLING_TIME),
    []
  )

  useEffect(() => {
    if (query) {
      debouncedOrganisationsQuery(query)
    }
  }, [query, debouncedOrganisationsQuery])

  return {
    searchResults,
    query,
    isLoading,
    setQuery
  }
}
